import React, { useState } from 'react';
import { Drawer, Table, Tag, Tooltip, DatePicker, Button, Statistic, Row, Col, Badge } from 'antd';
import { useLazyQuery } from '@apollo/client';
import { getCronGenerationLogsQuery } from '../../util/query/mobileResponse.query.js';
import dayjs from 'dayjs';
import { CheckCircleOutlined, CloseCircleOutlined, MinusCircleOutlined, SyncOutlined, ClockCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';

const { RangePicker } = DatePicker;

// ── Status config ─────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  success: { color: '#10b981', bg: '#ecfdf5', border: '#a7f3d0', icon: <CheckCircleOutlined />,   label: 'Success'  },
  partial: { color: '#f59e0b', bg: '#fffbeb', border: '#fde68a', icon: <ExclamationCircleOutlined />, label: 'Partial' },
  failed:  { color: '#ef4444', bg: '#fef2f2', border: '#fecaca', icon: <CloseCircleOutlined />,   label: 'Failed'   },
  skipped: { color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb', icon: <MinusCircleOutlined />,   label: 'Skipped'  },
};

const ENTRY_STATUS = {
  created: { color: '#10b981', bg: '#ecfdf5', label: 'Created' },
  skipped: { color: '#f59e0b', bg: '#fffbeb', label: 'Skipped' },
  failed:  { color: '#ef4444', bg: '#fef2f2', label: 'Failed'  },
};

// ── Sub-table (expanded duties) ───────────────────────────────────────────────
const EntrySubTable = ({ entries, dutyTypeName }) => {
  const entryColumns = [
    {
      title: 'Duty Type',
      key: 'dutyType',
      width: 160,
      render: () => (
        <span style={{ fontWeight: 500, color: '#374151' }}>{dutyTypeName || '—'}</span>
      ),
    },
    {
      title: 'Start Time',
      dataIndex: 'startTime',
      key: 'startTime',
      width: 155,
      render: v => (
        <span style={{ color: '#374151' }}>
          <ClockCircleOutlined style={{ color: '#6b7280', marginRight: 6 }} />
          {dayjs(v).format('ddd DD/MM/YY HH:mm')}
        </span>
      ),
    },
    {
      title: 'End Time',
      dataIndex: 'endTime',
      key: 'endTime',
      width: 155,
      render: v => (
        <span style={{ color: '#374151' }}>
          <ClockCircleOutlined style={{ color: '#6b7280', marginRight: 6 }} />
          {dayjs(v).format('ddd DD/MM/YY HH:mm')}
        </span>
      ),
    },
    {
      title: 'Duration',
      key: 'duration',
      width: 100,
      render: (_, e) => {
        const hrs = dayjs(e.endTime).diff(dayjs(e.startTime), 'hour');
        const mins = dayjs(e.endTime).diff(dayjs(e.startTime), 'minute') % 60;
        return <span style={{ color: '#6b7280', fontSize: 13 }}>{hrs > 0 ? `${hrs}h ` : ''}{mins}m</span>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: s => {
        const cfg = ENTRY_STATUS[s] ?? { color: '#6b7280', bg: '#f9fafb', label: s };
        return (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: cfg.bg, color: cfg.color,
            padding: '3px 10px', borderRadius: 20,
            fontSize: 12, fontWeight: 600,
          }}>
            {s === 'created' ? <CheckCircleOutlined /> : s === 'failed' ? <CloseCircleOutlined /> : <MinusCircleOutlined />}
            {cfg.label}
          </span>
        );
      },
    },
    {
      title: 'Detail',
      key: 'detail',
      render: (_, e) => e.reason
        ? <span style={{ color: '#ef4444', fontSize: 13 }}>{e.reason}</span>
        : e.dutyId
          ? <span style={{ color: '#9ca3af', fontSize: 12, fontFamily: 'monospace' }}>ID: {e.dutyId}</span>
          : '—',
    },
  ];

  return (
    <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 16px', margin: '4px 0' }}>
      <Table
        rowKey={(_, i) => i}
        dataSource={entries ?? []}
        columns={entryColumns}
        size="small"
        pagination={false}
        rowClassName={(r) => r.status === 'failed' ? 'cron-entry-failed' : ''}
        style={{ borderRadius: 6 }}
      />
    </div>
  );
};

// ── Main log row renderer ─────────────────────────────────────────────────────
const LogRow = ({ record }) => {
  const cfg = STATUS_CONFIG[record.runStatus] ?? STATUS_CONFIG.skipped;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ color: cfg.color, fontSize: 16 }}>{cfg.icon}</span>
      <span style={{ fontWeight: 600, color: '#111827' }}>{record.siteName || '—'}</span>
    </div>
  );
};

// ── Summary bar ───────────────────────────────────────────────────────────────
const SummaryBar = ({ logs }) => {
  const all = logs ?? [];
  const totalRuns     = all.length;
  const totalCreated  = all.reduce((s, l) => s + (l.createdCount ?? 0), 0);
  const totalSkipped  = all.reduce((s, l) => s + (l.skippedCount ?? 0), 0);
  const totalFailed   = all.reduce((s, l) => s + (l.failedCount  ?? 0), 0);

  return (
    <Row gutter={12} style={{ marginBottom: 20 }}>
      {[
        { label: 'Total Runs',    value: totalRuns,    color: '#6366f1', bg: '#eef2ff' },
        { label: 'Duties Created', value: totalCreated, color: '#10b981', bg: '#ecfdf5' },
        { label: 'Skipped',        value: totalSkipped, color: '#f59e0b', bg: '#fffbeb' },
        { label: 'Failed',         value: totalFailed,  color: '#ef4444', bg: '#fef2f2' },
      ].map(({ label, value, color, bg }) => (
        <Col span={6} key={label}>
          <div style={{
            background: bg, border: `1px solid ${color}22`,
            borderRadius: 10, padding: '12px 16px', textAlign: 'center',
          }}>
            <div style={{ fontSize: 24, fontWeight: 700, color }}>{value}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{label}</div>
          </div>
        </Col>
      ))}
    </Row>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const CronGenerationLogsDrawer = ({ visible, onClose }) => {
  const [page, setPage]           = useState(1);
  const [dateRange, setDateRange] = useState(null);

  const [fetchLogs, { data, loading }] = useLazyQuery(getCronGenerationLogsQuery, {
    fetchPolicy: 'cache-and-network',
  });

  const load = (p = 1, range = dateRange) => {
    fetchLogs({
      variables: {
        page: p,
        pageSize: 15,
        from: range?.[0] ? dayjs(range[0]).startOf('day').toISOString() : undefined,
        to:   range?.[1] ? dayjs(range[1]).endOf('day').toISOString()   : undefined,
      },
    });
    setPage(p);
  };

  React.useEffect(() => {
    if (visible) load(1, null);
  }, [visible]);

  const logs       = data?.getCronGenerationLogs?.response ?? [];
  const totalCount = data?.getCronGenerationLogs?.totalCount ?? 0;

  const columns = [
    {
      title: 'Run Info',
      key: 'run',
      render: (_, record) => {
        const cfg = STATUS_CONFIG[record.runStatus] ?? STATUS_CONFIG.skipped;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: cfg.bg, color: cfg.color,
                border: `1px solid ${cfg.border}`,
                padding: '2px 9px', borderRadius: 20,
                fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
              }}>
                {cfg.icon} {cfg.label.toUpperCase()}
              </span>
              <span style={{
                fontSize: 11, color: '#9ca3af',
                background: '#f3f4f6', padding: '2px 10px', borderRadius: 20, width:'max-content'
              }}>
                {record.jobName === 'scheduleDays' ? 'Schedule Days' : 'Frequency Days'}
              </span>
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
              <ClockCircleOutlined style={{ fontSize: 11 }} />
              {dayjs(record.runAt).format('ddd DD MMM YYYY [at] HH:mm:ss')}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Site',
      key: 'site',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600, color: '#111827', fontSize: 13 }}>{r.siteName || '—'}</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>{r.customerName || '—'}</div>
        </div>
      ),
    },
    {
      title: 'Duty Type',
      dataIndex: 'dutyTypeName',
      key: 'dutyTypeName',
      width: 150,
      render: v => v
        ? <span style={{ fontWeight: 500, color: '#374151', fontSize: 13 }}>{v}</span>
        : <span style={{ color: '#d1d5db' }}>—</span>,
    },
    {
      title: 'Duties',
      key: 'duties',
      width: 200,
      render: (_, r) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {r.createdCount > 0 && (
              <span style={{ background: '#ecfdf5', color: '#10b981', border: '1px solid #a7f3d0', padding: '2px 8px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                ✓ {r.createdCount} created
              </span>
            )}
            {r.skippedCount > 0 && (
              <Tooltip title="Already exists — same site, assignee & time">
                <span style={{ background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a', padding: '2px 8px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'help' }}>
                  ↷ {r.skippedCount} skipped
                </span>
              </Tooltip>
            )}
            {r.failedCount > 0 && (
              <span style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', padding: '2px 8px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                ✕ {r.failedCount} failed
              </span>
            )}
          </div>
          {r.entries?.length > 0 && (
            <div style={{ fontSize: 11, color: '#9ca3af' }}>
              {dayjs(r.entries[0].startTime).format('DD/MM/YY')}
              {r.entries.length > 1 && ` – ${dayjs(r.entries[r.entries.length - 1].startTime).format('DD/MM/YY')}`}
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <Drawer
      title={"Duty Generation Logs"}
      closable={true}
      placement="right"
      closeIcon={<FontAwesomeIcon icon={faClose} onClick={onClose} className='close-icon' style={{marginRight:'54px'}}/>}

      open={visible}
      onClose={onClose}
      width={900}
      destroyOnClose
      // styles={{ body: { padding: '20px 24px', background: '#f9fafb' }, header: { background: '#fff', borderBottom: '1px solid #e5e7eb' } }}
    >
      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <RangePicker
          size="middle"
          style={{ borderRadius: 8, flex: 1 }}
          onChange={range => { setDateRange(range); load(1, range); }}
        />
        <Button
          onClick={() => { setDateRange(null); load(1, null); }}
          style={{ borderRadius: 8, color: '#6b7280', border: '1px solid #e5e7eb' }}
        >
          Reset
        </Button>
      </div>

      {/* Summary cards */}
      <SummaryBar logs={logs} />

      {/* Main table */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <Table
          rowKey="_id"
          loading={loading}
          dataSource={logs}
          columns={columns}
          size="middle"
          pagination={{
            current: page,
            pageSize: 15,
            total: totalCount,
            showTotal: (t) => `${t} runs`,
            onChange: (p) => load(p),
            style: { padding: '12px 16px' },
          }}
          rowClassName={(r) => `cron-log-row cron-log-row--${r.runStatus}`}
          expandable={{
            expandedRowRender: (r) => <EntrySubTable entries={r.entries} dutyTypeName={r.dutyTypeName} />,
            rowExpandable: (r) => r.entries?.length > 0,
          }}
          style={{ borderRadius: 0 }}
        />
      </div>

      <style>{`
        .cron-log-row td { vertical-align: middle !important; }
        .cron-log-row--failed td:first-child { border-left: 3px solid #ef4444; }
        .cron-log-row--partial td:first-child { border-left: 3px solid #f59e0b; }
        .cron-log-row--success td:first-child { border-left: 3px solid #10b981; }
        .cron-log-row--skipped td:first-child { border-left: 3px solid #d1d5db; }
        .cron-entry-failed td { background: #fff5f5 !important; }
      `}</style>
    </Drawer>
  );
};

export default CronGenerationLogsDrawer;
